import { Service } from './Accounts';

export interface IUserInfo {
  id: number;
  Name: string;
  TypeID: number;
  Addresses: IAddress[];
  Phones: Phone[];
  Balance: number;
  Bonus: number;
  CreateDate: string;
  MultiAccount: MultiAccountType;
  EmailSettings: IEmailSettings;
  SmsSettings: ISmsSettings;
  PushSettings: IPushSettings;
  SocialAccounts: ISocialAccount[];
  Equipments: IEquipment[];
  Permissions: IPermission;
  HasUnreadPushes: boolean;
  Language: 'ru' | 'en' | 'ua';
  Entrepreneur: IEntrepreneur;
  Shares: IShares;
}

export interface IUserInfoTransform extends IUserInfo {
  сonnectedShare50Percent: IShare | null;
}

interface MultiAccountType {
  Type: 'family' | 'corporation';
}

export type MultiAccountStatus = 'verified' | 'unverified' | 'inactive';
export type MultiAccountRole = 'admin' | 'member';

export enum BannerActionTypes {
  OPEN_LINK = 'OPEN_LINK',
  CHANGE_SERVICE = 'CHANGE_SERVICE',
  TOP_UP = 'TOP_UP',
  DO_PAYMENT = 'DO_PAYMENT',
}

export interface IUserRepairWorks {
  id: number;
  PublicationDate: string;
  ShowOnSite: number;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  Reason: string;
}

export interface IUserBanner {
  id: number;
  StartDate: string;
  StopDate: string;
  Action: {
    Action: BannerActionTypes;
    Params: {
      LinkUrl?: string;
      accountID?: number;
      Amount?: string | number;
      isAllowUserChange?: boolean;
      serviceId?: string | number;
      serviceType?: string;
      PaymentCounts?: string | number;
      service?: Service;
    };
  };
  Images: Record<
    'desktop' | 'tablet' | 'mobile',
    {
      bannerLanguage: string;
      filename: string;
    }
  >;
}

export interface IMultiAccount {
  Alias: string | null;
  CreateDate: string;
  Invite: { CreateDate: string; ExpireDate: string; Status: MultiAccountStatus };
  MultiAccountID: number;
  Role: MultiAccountRole;
  Service: null | { ExpireDate: string; Status: number };
  Status: MultiAccountStatus;
  UseAutoPay: boolean;
  Priority: number;
  User: null | { Address: string; Name: string };
  UserID: number;
  id: number;
}

export interface IInvite {
  id: number;
  ExpireDate: string;
  NotifyChannels: {
    email: boolean;
    sms: boolean;
  };
  ttl: number;
}

export interface ILinkAccount {
  id: number;
  MultiAccountID: number;
  UserID: number;
  Role: MultiAccountRole;
  Status: MultiAccountStatus;
  Invite: IInvite;
}

export interface IMultiAccounts {
  Account: null | IMultiAccount;
  List: IMultiAccount[];
}

export interface IShares {
  Activated: [] | IShare[];
}

export interface IShare {
  ActivationDate: Date;
  ActiveFrom: Date;
  ActiveTo: string;
  Content: string;
  Name: string;
  ShortContent: string;
  Type: string;
  id: number;
}

export interface IAddress {
  id: number;
  Street: IStreet;
  House: string;
  Flat: string;
  LocationType: {
    id: number;
    Name: string;
  };
}

export interface IStreet {
  NameRu: string;
  NameUa: string;
  NameEn: string;
}

export interface IEmailSettings {
  Email: string;
  Accept: boolean;
  NoBalance: boolean;
  News: boolean;
  Repairs: boolean;
  IsBlocked: boolean;
  IsConfirmed: boolean;
  CreateDate: string;
}

export interface ISmsSettings {
  Phone: string;
  Accept: boolean;
  NoBalance: boolean;
  News: boolean;
  Repairs: boolean;
  IsBlocked: boolean;
  CreateDate: string;
}

export interface ISocialAccount {
  Network: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Picture: string;
  CreateDate: string;
}

export interface IEquipment {
  title: string;
  serial: string;
  warranty: number;
  set: string;
  rent: boolean;
}

export interface IPushSettings {
  Accept: boolean;
  NoBalance: boolean;
  News: boolean;
  Repairs: boolean;
}

export interface IUserDocument {
  id: number;
  name: string;
  url: {
    show: string;
    download: string;
  };
  date: string;
}

export interface IAppSettings {
  feature: {
    digitalDocuments: boolean;
    multiaccount: boolean;
  };
}

export interface IPermission {
  Info: {
    ShowBonuses: boolean;
  };
  Payments: {
    AddMoney: boolean;
    PrepayDiscount: boolean;
    RegularPayment: boolean;
  };
  Services: {
    Activate: boolean;
    Change: boolean;
    Suspend: boolean;
    Internet: {
      CanEdit: boolean;
    };
    Iptv: {
      CanAdd: boolean;
      CanEdit: boolean;
      CanRemove: boolean;
      BrizTVAvailable: boolean;
    };
  };
  ServicesEx: {
    Activate: boolean;
  };
}

export interface IEntrepreneur {
  PlatonKey: string;
  PlatonPassword: string;
}

export interface Phone {
  id: number;
  value: string;
}

export type AvailableLanguageLocale = 'Ru' | 'Ua' | 'En';
